import React, { Component, Fragment } from 'react';
import { UserProductGroup } from '~/Api/User/ProductGroup';
import { DebounceInput } from '~/Debounce';
import ActivityIndicator from '~/UI/ActivityIndicator';
import Input from '~/UI/Input/Input';
import filterInputStyles from '~/UI/SegmentedSelect/filterInput.less';
import largeSegmentedSelectStyles from '~/UI/SegmentedSelect/large.less';
import SegmentedSelectOption from '~/UI/SegmentedSelect/Option/SegmentedSelectOption';
import SegmentedSelectPage from '~/UI/SegmentedSelect/Page/SegmentedSelectPage';
import SegmentedSelectSegment from '~/UI/SegmentedSelect/Segment/SegmentedSelectSegment';
import SegmentedSelect from '~/UI/SegmentedSelect/SegmentedSelect';

import GroupSelectModule from './GroupSelectModule';
import GroupSelectOption from './GroupSelectOption';
import styles from './styles.less';

export interface SelectedOption {
	value: UserProductGroup;
	parentValue: any;
	prompt: React.ReactNode | null;
}

export interface Props {
	selectedOption: SelectedOption | null;
	groups: UserProductGroup[];
	recentGroups: UserProductGroup[];
	subGroups: UserProductGroup[];
	nameFilter: string;
	isFetching: boolean;
	isFetchingSubGroups: boolean;
	disabled: boolean;
	hasMore: boolean;
	hasMoreSubGroups: boolean;
	groupsHeader: React.ReactNode;
	groupHeader: React.ReactNode;
	selectRef?(select: SegmentedSelect | null): void;
	onLoad(): void;
	onFetch(a: string | null, b: string): void;
	onSelect(option: SelectedOption | null): void;
	onFilter(a: string): void;
}

export default class GroupSelect extends Component<Props> {
	static defaultProps: Pick<
		Props,
		| 'groupsHeader'
		| 'groupHeader'
		| 'groups'
		| 'hasMore'
		| 'hasMoreSubGroups'
		| 'isFetching'
		| 'isFetchingSubGroups'
		| 'selectedOption'
		| 'recentGroups'
		| 'subGroups'
	> = {
		groupsHeader: null,
		groupHeader: null,
		groups: [],
		hasMore: false,
		hasMoreSubGroups: false,
		isFetching: false,
		isFetchingSubGroups: false,
		selectedOption: null,
		recentGroups: [],
		subGroups: [],
	};

	componentDidMount() {
		this.props.onLoad();
	}

	componentDidUpdate(prevProps: Props) {
		const { selectedOption, groups, subGroups, nameFilter, onFetch, onSelect } = this.props;

		if (nameFilter !== prevProps.nameFilter) {
			onFetch(null, nameFilter);
		}

		if (selectedOption === null) {
			return;
		}

		if (
			!selectedOption.parentValue &&
			prevProps.groups !== groups &&
			groups.find((group) => group.id === selectedOption.value.id) === undefined
		) {
			onSelect(null);
		} else if (prevProps.selectedOption !== selectedOption && selectedOption.value.isGroup()) {
			onFetch(selectedOption.value.id, '');
		} else if (
			prevProps.subGroups !== subGroups &&
			(prevProps.subGroups.length > 0 || subGroups.length > 0) &&
			selectedOption.parentValue !== selectedOption.value &&
			subGroups.find((group) => group.id === selectedOption.value.id) === undefined
		) {
			onSelect(null);
		}
	}
	renderOptions(groups: UserProductGroup[], displayModule: boolean, displayParentName: boolean) {
		return groups.map((group) => (
			<SegmentedSelectOption hasSubOptions={group.isGroup()} key={group.id} prompt={group.name} value={group}>
				<GroupSelectOption displayModule={displayModule} displayParentName={displayParentName} group={group} />
			</SegmentedSelectOption>
		));
	}

	renderTitle(selectedOption: SelectedOption | null) {
		if (!selectedOption || !selectedOption.parentValue) {
			return null;
		}

		return (
			<Fragment>
				{selectedOption.parentValue.name}
				<GroupSelectModule group={selectedOption.parentValue} style={styles.titleModule} />
			</Fragment>
		);
	}

	renderFilterInput() {
		const { nameFilter, disabled, onFilter } = this.props;

		return (
			<DebounceInput
				delayMilliseconds={200}
				value={nameFilter}
				onBlur={() => undefined}
				onChange={(value) => onFilter(value)}
			>
				<Input autoFocus disabled={disabled} styles={filterInputStyles} type='search' />
			</DebounceInput>
		);
	}

	fetchGroups = (page: number) => {
		if (page > 0) {
			const { onFetch, nameFilter } = this.props;
			onFetch(null, nameFilter);
		}
	};

	fetchSubGroups = () => {
		const { selectedOption, onFetch } = this.props;

		if (selectedOption?.value.isGroup()) {
			onFetch(selectedOption.value.id, '');
		}
	};

	render() {
		const {
			selectedOption,
			groups,
			recentGroups,
			subGroups,
			nameFilter,
			isFetching,
			isFetchingSubGroups,
			disabled,
			hasMore,
			hasMoreSubGroups,
			groupsHeader,
			groupHeader,
			selectRef,
			onSelect,
		} = this.props;

		return (
			<SegmentedSelect
				dataTestId='group-select-segmented-select'
				disabled={disabled}
				filterInput={this.renderFilterInput()}
				popoverWidth={800}
				prompt='Choose product list'
				ref={selectRef ? (ref) => selectRef(ref) : null}
				selectedOption={selectedOption}
				styles={largeSegmentedSelectStyles}
				onSelect={onSelect}
			>
				<SegmentedSelectPage
					content={isFetching ? <ActivityIndicator /> : null}
					hasMore={hasMore}
					header={groupsHeader}
					onFetch={this.fetchGroups}
				>
					<SegmentedSelectSegment title='Recent lists'>
						{this.renderOptions(recentGroups, false, false)}
					</SegmentedSelectSegment>

					<SegmentedSelectSegment
						subTitle={
							groups.length === 0 && nameFilter !== '' ? (
								<p className={styles.subTitle}>No lists found. Please redefine your query.</p>
							) : null
						}
						title={nameFilter !== '' ? 'Filtered lists' : 'All lists'}
					>
						{this.renderOptions(groups, true, nameFilter !== '')}
					</SegmentedSelectSegment>
				</SegmentedSelectPage>

				<SegmentedSelectPage
					content={isFetchingSubGroups ? <ActivityIndicator /> : null}
					hasMore={hasMoreSubGroups}
					header={groupHeader}
					onFetch={this.fetchSubGroups}
				>
					<SegmentedSelectSegment highlightTitle title={this.renderTitle(selectedOption)}>
						{this.renderOptions(subGroups, false, false)}
					</SegmentedSelectSegment>
				</SegmentedSelectPage>
			</SegmentedSelect>
		);
	}
}
